import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Archive,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  Clapperboard,
  Coins,
  ExternalLink,
  FileText,
  FolderOpen,
  GalleryVerticalEnd,
  Image as ImageIcon,
  LogOut,
  Languages,
  LifeBuoy,
  LoaderCircle,
  Scale,
  MoreHorizontal,
  Plus,
  ReceiptText,
  RefreshCw,
  SlidersHorizontal,
  Square,
  SquareTerminal,
  Terminal as ShellIcon,
  Trash2,
  Upload,
} from "lucide-react";
import type {
  Account,
  Asset,
  Film,
  Preview,
  Project,
  ProjectDetail,
  ProjectSummary,
  StudioConfig,
  StudioEvent,
} from "../shared/types";
import { api, formatSize, formatTime, patch, post, uploadFiles } from "./api";
import {
  Button,
  DropVeil,
  FilmPlayer,
  IconButton,
  LanguageSwitch,
  PreviewPlayer,
  Loading,
  Logo,
  Mark,
  Modal,
  ProductTile,
  Ring,
  relativeTime,
  useFileDrop,
  useMedia,
} from "./components";
import { CreditsScreen, useWallet } from "./Credits";
import { AccountPanel, Redeem } from "./Account";
import { Legal } from "./Legal";
import { CheckoutReturn, day, exVat, PlansScreen, PublicPlans } from "./Plans";
import { SenalHint, useSenal } from "./Senal";
import { VideoSettings, VideoSummary } from "./VideoSettings";
import { Home } from "./Home";
import { InlineTitle, ModeSwitch, rememberMode } from "./Creation";
import { RingField } from "./fx";
import { PromoPhone } from "./Promo";
import { Landing } from "./Landing";
import { track, watchPage } from "./usage";
import { MakingScreen, MakingThumb, Trace, useMaking } from "./Making";
import { creditNumber, PLANS } from "../shared/catalog";
import { unrendered } from "../shared/versions";
import { LINE, lineText, type Place } from "../shared/line";
import { bilingual, lang, text, useLang, useText } from "./i18n";

const TEXT = bilingual({
  en: {
    welcome: {
      title: "Your product,",
      focus: "in focus.",
      preview: "Local preview",
      explore: "Explore the local studio",
      previewNote: "Recorded examples. No account or payment.",
      invite: "Invite-only early access.",
      plans: "Pricing",
    },
    account: {
      language: "Language",
      preview: "Local preview",
      previewNote: "Recorded examples. No paid generation.",
      ends: (date: string) => `Ends on ${date}`,
      renews: (credits: string, date: string) =>
        `${credits} credits a month · renews ${date}`,
      noPlan: "Plans add credits every month.",
      plans: "Plans",
      seePlans: "See plans",
      billing: "Subscription and invoices",
      invoices: "Invoices",
      help: "Help",
      legal: "Terms and privacy",
      signOut: "Sign out",
    },
    app: {
      planUpdated: "Your plan is updated.",
      slow: "The studio is taking a moment.",
      retry: "Try again",
      lost: "This page is out of focus.",
      back: "Back to studio",
      dismiss: "Dismiss notification",
    },
    nav: {
      primary: "Primary",
      studio: "Studio",
      videos: "Videos",
      credits: "Credits",
      you: "You",
      home: "Studio home",
      newVideo: "New video",
      projects: "Projects",
      previewPill: "Local preview · no paid generation",
      choosePlan: "Choose a plan",
      from: (price: string) => `From ${price} a month`,
    },
    plan: {
      preview: "Preview",
      named: (name: string) => `${name} plan`,
      active: "Active plan",
      none: "No plan yet",
    },
    library: {
      title: "Videos",
      filter: "Filter by project",
      all: "All",
      preview: "Preview",
      example: "Example",
      empty: "No videos yet.",
      make: "Make a video",
      downloadMp4: "Download MP4",
    },
    project: {
      deleted: "Video deleted.",
      added: (n: number) => `${n} ${n === 1 ? "file" : "files"} added.`,
      failed: "We couldn’t open this project.",
      back: "Back to studio",
      example: "Example",
      assets: "Project assets",
      settings: "Video settings",
      actions: "Project actions",
      another: "Make another video",
      archive: "Archive project",
      unarchive: "Unarchive project",
      delete: "Delete video",
      drop: "Drop to add to this video",
      workspace: "Workspace",
      env: {
        docker: "local Docker",
        preview: "local preview",
        remote: "workspace",
      },
      shell: "Shell",
      side: "Video and assets",
      video: "Video",
      newVersion: ", new version",
      work: "Work",
      opening: "Opening terminal…",
      refresh: "Refresh videos",
    },
    chat: {
      history: "Conversation history",
      empty: "What should this video sell?",
      director: "Director",
      // Each chip writes a request to the director in the customer's words.
      shorter: { label: "Make it shorter", ask: "Make a shorter cut." },
      opening: { label: "Another opening", ask: "Try a different opening." },
      language: {
        label: "Another language",
        ask: "Make a version of this video in ",
      },
      read: {
        label: "Another read",
        ask: "Try another read of the voiceover.",
      },
      feature: "Spotlight a feature",
      series: "Start a series",
      attach: "Attach files",
      message: "Message your director",
      change: "Ask for a change",
      first: "Paste a product link and tell me what you need",
      stop: "Stop generation",
      send: "Send message",
      reconnecting: "Reconnecting…",
    },
    stage: {
      room: "Screening room",
      first: "Your first version lands here",
      versions: "Versions",
      version: (n: number, preview: boolean) =>
        `Version ${n}${preview ? ", preview" : ""}`,
      details: "Project details",
      preparing: (percent: number) => `Preparing video ${percent}%`,
      preview: (title: string) => `Preview · ${title}`,
      none: "No versions yet.",
      settings: "Video settings",
    },
    assets: {
      title: "Assets",
      all: "All",
      add: "Add",
      drop: "Drop assets",
      adding: "Adding…",
      choose: "Drop or choose files",
      kinds: "Screens · logo · photos · a short to riff on",
    },
    remove: {
      title: "Delete this video?",
      note: "Its chat, files and videos are removed for good.",
      cancel: "Cancel",
      busy: "Deleting…",
      confirm: "Delete video",
    },
    download: {
      video: "Download video",
      preparing: "Preparing your video",
      again: "Try the download again",
    },
  },
  es: {
    welcome: {
      title: "Tu producto,",
      focus: "en foco.",
      preview: "Vista previa local",
      explore: "Explorar el estudio local",
      previewNote: "Ejemplos grabados. Sin cuenta ni pago.",
      invite: "Acceso anticipado solo por invitación.",
      plans: "Precios",
    },
    account: {
      language: "Idioma",
      preview: "Vista previa local",
      previewNote: "Ejemplos grabados. Sin generación de pago.",
      ends: (date) => `Termina el ${date}`,
      renews: (credits, date) =>
        `${credits} créditos al mes · se renueva el ${date}`,
      noPlan: "Los planes suman créditos cada mes.",
      plans: "Planes",
      seePlans: "Ver planes",
      billing: "Suscripción y facturas",
      invoices: "Facturas",
      help: "Ayuda",
      legal: "Términos y privacidad",
      signOut: "Cerrar sesión",
    },
    app: {
      planUpdated: "Tu plan se ha actualizado.",
      slow: "El estudio está tardando un poco.",
      retry: "Reintentar",
      lost: "Esta página está desenfocada.",
      back: "Volver al estudio",
      dismiss: "Cerrar aviso",
    },
    nav: {
      primary: "Principal",
      studio: "Estudio",
      videos: "Vídeos",
      credits: "Créditos",
      you: "Tú",
      home: "Inicio del estudio",
      newVideo: "Nuevo vídeo",
      projects: "Proyectos",
      previewPill: "Vista previa local · sin generación de pago",
      choosePlan: "Elige un plan",
      from: (price) => `Desde ${price} al mes`,
    },
    plan: {
      preview: "Vista previa",
      named: (name) => `Plan ${name}`,
      active: "Plan activo",
      none: "Sin plan todavía",
    },
    library: {
      title: "Vídeos",
      filter: "Filtrar por proyecto",
      all: "Todos",
      preview: "Vista previa",
      example: "Ejemplo",
      empty: "Aún no hay vídeos.",
      make: "Crear un vídeo",
      downloadMp4: "Descargar MP4",
    },
    project: {
      deleted: "Vídeo eliminado.",
      added: (n) =>
        n === 1 ? "1 archivo añadido." : `${n} archivos añadidos.`,
      failed: "No hemos podido abrir este proyecto.",
      back: "Volver al estudio",
      example: "Ejemplo",
      assets: "Archivos del proyecto",
      settings: "Ajustes del vídeo",
      actions: "Acciones del proyecto",
      another: "Crear otro vídeo",
      archive: "Archivar proyecto",
      unarchive: "Desarchivar proyecto",
      delete: "Eliminar vídeo",
      drop: "Suelta para añadir a este vídeo",
      workspace: "Estudio",
      env: {
        docker: "Docker local",
        preview: "vista previa local",
        remote: "estudio",
      },
      shell: "Shell",
      side: "Vídeo y archivos",
      video: "Vídeo",
      newVersion: ", versión nueva",
      work: "Trabajo",
      opening: "Abriendo terminal…",
      refresh: "Actualizar vídeos",
    },
    chat: {
      history: "Historial de la conversación",
      empty: "¿Qué debe vender este vídeo?",
      director: "Director",
      shorter: { label: "Más corto", ask: "Haz un montaje más corto." },
      opening: { label: "Otro inicio", ask: "Prueba un inicio distinto." },
      language: {
        label: "Otro idioma",
        ask: "Haz una versión de este vídeo en ",
      },
      read: {
        label: "Otra locución",
        ask: "Prueba otra lectura de la locución.",
      },
      feature: "Destacar una función",
      series: "Empezar una serie",
      attach: "Adjuntar archivos",
      message: "Mensaje para tu director",
      change: "Pide un cambio",
      first: "Pega el enlace de un producto y dime qué necesitas",
      stop: "Detener la generación",
      send: "Enviar mensaje",
      reconnecting: "Reconectando…",
    },
    stage: {
      room: "Sala de proyección",
      first: "Aquí aparecerá tu primera versión",
      versions: "Versiones",
      version: (n, preview) => `Versión ${n}${preview ? ", vista previa" : ""}`,
      details: "Detalles del proyecto",
      preparing: (percent) => `Preparando el vídeo ${percent}%`,
      preview: (title) => `Vista previa · ${title}`,
      none: "Aún no hay versiones.",
      settings: "Ajustes del vídeo",
    },
    assets: {
      title: "Archivos",
      all: "Todos",
      add: "Añadir",
      drop: "Suelta archivos",
      adding: "Añadiendo…",
      choose: "Suelta o elige archivos",
      kinds: "Capturas · logo · fotos · un vídeo corto de referencia",
    },
    remove: {
      title: "¿Eliminar este vídeo?",
      note: "Su chat, sus archivos y sus vídeos se eliminan para siempre.",
      cancel: "Cancelar",
      busy: "Eliminando…",
      confirm: "Eliminar vídeo",
    },
    download: {
      video: "Descargar vídeo",
      preparing: "Preparando tu vídeo",
      again: "Reintentar la descarga",
    },
  },
});
const TerminalPanel = lazy(() =>
  import("./TerminalPanel").then((m) => ({ default: m.TerminalPanel })),
);
const MessageContent = lazy(() => import("./MessageContent"));

type Notice = (message: string) => void;

function useProjects(enabled: boolean, revision: number) {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null),
    [failed, setFailed] = useState(false),
    [attempt, setAttempt] = useState(0);
  const location = useLocation();
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api<{ projects: ProjectSummary[] }>("/projects")
      .then((d) => {
        if (cancelled) return;
        setProjects(d.projects);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, revision, attempt, location.pathname]);
  return { projects, failed, retry: () => setAttempt((n) => n + 1) };
}

export function App() {
  const [account, setAccount] = useState<Account | null>(null),
    [config, setConfig] = useState<StudioConfig | null>(null),
    [loaded, setLoaded] = useState(false),
    [error, setError] = useState(""),
    [toast, setToast] = useState(""),
    [accountSheet, setAccountSheet] = useState(false),
    [revision, setRevision] = useState(0);
  const t = useText(TEXT);
  const navigate = useNavigate(),
    location = useLocation();
  const notify: Notice = useCallback((message) => setToast(message), []);
  // Back from Stripe: a checkout to confirm, or a plan change to reflect.
  const [params, setParams] = useSearchParams();
  const [checkout] = useState(() => {
    const session = params.get("checkout");
    return session && /^cs_\w+$/.test(session) ? session : "";
  });
  const [checkoutOpen, setCheckoutOpen] = useState(!!checkout);
  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);
  useEffect(() => {
    if (!params.has("checkout") && !params.has("plan")) return;
    const changed = params.get("plan") === "changed";
    const next = new URLSearchParams(params);
    next.delete("checkout");
    next.delete("plan");
    setParams(next, { replace: true });
    if (!changed) return;
    setToast(text(TEXT).app.planUpdated);
    // Stripe tells the studio moments later; follow it.
    const timers = [0, 2500, 6000].map((ms) =>
      setTimeout(
        () =>
          void api<{ account: Account | null }>("/auth/me").then(
            (u) => u.account && setAccount(u.account),
          ),
        ms,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [params, setParams]);
  const changed = useCallback(() => setRevision((r) => r + 1), []);
  const { projects, failed, retry } = useProjects(!!account, revision);
  useEffect(() => {
    void Promise.all([
      api<{ account: Account | null }>("/auth/me"),
      api<StudioConfig>("/config"),
    ])
      .then(([u, c]) => {
        setAccount(u.account);
        setConfig(c);
        setLoaded(true);
      })
      .catch((e) => {
        setError(e.message);
        setLoaded(true);
      });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 6000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k" && account) {
        e.preventDefault();
        navigate("/new");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [account, navigate]);
  if (location.pathname === "/legal") return <Legal />;
  if (!loaded) return <Loading />;
  if (error)
    return (
      <div className="fatal">
        <Logo />
        <h1 className="h1">{t.app.slow}</h1>
        <p>{error}</p>
        <Button className="primary" onClick={() => window.location.reload()}>
          {t.app.retry}
        </Button>
      </div>
    );
  if (!account && config && location.pathname === "/plans")
    return <PublicPlans config={config} />;
  if (!account && config && location.pathname === "/")
    return <Landing config={config} />;
  if (!account || !config)
    return (
      <SignIn
        config={config!}
        onLogin={(a, notice) => {
          setAccount(a);
          if (notice) setToast(notice);
        }}
      />
    );
  const inProject = location.pathname.startsWith("/project/");
  const studio = (
    <Home
      account={account}
      config={config}
      projects={projects}
      failed={failed}
      onRetry={retry}
      onCreated={changed}
    />
  );
  return (
    <div className={`app ${inProject ? "in-project" : ""}`}>
      <Sidebar
        account={account}
        config={config}
        projects={projects}
        onAccount={() => setAccountSheet(true)}
      />
      <main className="main">
        <Routes>
          <Route path="/" element={studio} />
          <Route path="/new" element={studio} />
          {/* signed in from the sign-in or sign-up page: the studio */}
          <Route path="/entrar" element={<Navigate to="/" replace />} />
          <Route path="/empezar" element={<Navigate to="/" replace />} />
          <Route
            path="/canjear"
            element={<Redeem account={account} onDone={setToast} />}
          />
          <Route
            path="/credits"
            element={
              <CreditsScreen
                account={account}
                config={config}
                onError={notify}
              />
            }
          />
          <Route
            path="/plans"
            element={
              <PlansScreen account={account} config={config} onError={notify} />
            }
          />
          <Route
            path="/films"
            element={<Library projects={projects} onError={notify} />}
          />
          <Route
            path="/project/:projectId"
            element={
              <ProjectScreen
                key={`${account.id}:${location.pathname}`}
                account={account}
                config={config}
                onError={notify}
                onChange={changed}
              />
            }
          />
          <Route
            path="*"
            element={
              <div className="not-found">
                <Mark />
                <h1 className="h1">{t.app.lost}</h1>
                <Link className="button ghost" to="/">
                  {t.app.back} <ArrowRight size={16} />
                </Link>
              </div>
            }
          />
        </Routes>
      </main>
      {!inProject && (
        <nav className="tabbar" aria-label={t.nav.primary}>
          <NavLink to="/" end className="tab">
            <Mark /> {t.nav.studio}
          </NavLink>
          <NavLink to="/films" className="tab">
            <GalleryVerticalEnd size={22} /> {t.nav.videos}
          </NavLink>
          <NavLink to="/credits" className="tab">
            <Coins size={22} /> {t.nav.credits}
          </NavLink>
          <button className="tab" onClick={() => setAccountSheet(true)}>
            <span className="avatar">{account.name.slice(0, 1)}</span>{" "}
            {t.nav.you}
          </button>
        </nav>
      )}
      {accountSheet && (
        <AccountSheet
          account={account}
          config={config}
          onClose={() => setAccountSheet(false)}
          onError={notify}
          onSignedOut={() => {
            setAccount(null);
            setAccountSheet(false);
            navigate("/");
          }}
        />
      )}
      {checkout && checkoutOpen && (
        <CheckoutReturn
          account={account}
          session={checkout}
          onAccount={setAccount}
          onNotice={notify}
          onDone={closeCheckout}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <span>{toast}</span>
          <IconButton label={t.app.dismiss} onClick={() => setToast("")}>
            ×
          </IconButton>
        </div>
      )}
    </div>
  );
}

function Sidebar({
  account,
  config,
  projects,
  onAccount,
}: {
  account: Account;
  config: StudioConfig;
  projects: ProjectSummary[] | null;
  onAccount: () => void;
}) {
  const t = useText(TEXT);
  const navigate = useNavigate();
  const { wallet } = useWallet();
  const active = (projects || []).filter((p) => !p.archived);
  return (
    <aside className="side">
      <div className="side-top">
        <Link to="/" aria-label={t.nav.home}>
          <Logo />
        </Link>
        <IconButton label={t.nav.newVideo} onClick={() => navigate("/new")}>
          <Plus size={19} />
        </IconButton>
      </div>
      <nav className="side-nav" aria-label={t.nav.primary}>
        <NavLink to="/" end className="nv">
          <Mark className="nv-mark" /> {t.nav.studio}
        </NavLink>
        <NavLink to="/films" className="nv">
          <GalleryVerticalEnd size={18} /> {t.nav.videos}
        </NavLink>
        <NavLink to="/credits" className="nv">
          <Coins size={18} /> {t.nav.credits}
          <span className="nv-end mono">
            {wallet ? creditNumber(wallet.available, lang()) : ""}
          </span>
        </NavLink>
      </nav>
      {active.length > 0 && (
        <>
          <div className="side-sec">{t.nav.projects}</div>
          <nav className="side-proj scroll" aria-label={t.nav.projects}>
            {active.map((p) => (
              <NavLink to={`/project/${p.id}`} className="pj" key={p.id}>
                <ProductTile name={p.brand || p.title} />
                <span className="pj-name">{p.title}</span>
                {p.status === "working" ? (
                  <Ring />
                ) : (
                  <span className="pj-n">{p.versionCount || ""}</span>
                )}
              </NavLink>
            ))}
          </nav>
        </>
      )}
      <div className="side-foot">
        {config.preview && (
          <span className="preview-pill">{t.nav.previewPill}</span>
        )}
        {account.plan === "inactive" && (
          <Link to="/plans" className="side-plans">
            <span>
              <b>{t.nav.choosePlan}</b>
              <small>{t.nav.from(exVat(PLANS.starter.price))}</small>
            </span>
            <ArrowRight size={15} />
          </Link>
        )}
        <button className="account-button" onClick={onAccount}>
          <span className="avatar">{account.name.slice(0, 1)}</span>
          <span>
            <b>{account.name}</b>
            <small>{planLabel(account, t.plan)}</small>
          </span>
          <ChevronDown size={15} />
        </button>
      </div>
    </aside>
  );
}

const planLabel = (account: Account, t: (typeof TEXT)["en"]["plan"]) =>
  account.plan === "preview"
    ? t.preview
    : account.subscription
      ? t.named(PLANS[account.subscription.tier].name)
      : account.plan === "active"
        ? t.active
        : t.none;

function AccountSheet({
  account,
  config,
  onClose,
  onError,
  onSignedOut,
}: {
  account: Account;
  config: StudioConfig;
  onClose: () => void;
  onError: Notice;
  onSignedOut: () => void;
}) {
  const t = useText(TEXT);
  const [opening, setOpening] = useState(false);
  // Invoices, payment details, plan changes and cancellation live in Stripe's portal.
  const portal = () => {
    setOpening(true);
    void post("/billing/portal")
      .then(({ url }) => window.location.assign(url))
      .catch((e) => {
        onError(e.message);
        setOpening(false);
      });
  };
  return (
    <Modal title={account.name} subtitle={account.email} onClose={onClose}>
      <div className="plan">
        <div>
          <b>
            {config.preview ? t.account.preview : planLabel(account, t.plan)}
          </b>
          <small>
            {config.preview
              ? t.account.previewNote
              : account.subscription
                ? account.subscription.cancelling
                  ? t.account.ends(day(account.subscription.renews))
                  : t.account.renews(
                      creditNumber(
                        PLANS[account.subscription.tier].credits,
                        lang(),
                      ),
                      day(account.subscription.renews),
                    )
                : t.account.noPlan}
          </small>
        </div>
        {!config.preview && (
          <Link
            className={`button ${account.plan === "active" ? "ghost" : "primary"}`}
            to="/plans"
            onClick={onClose}
          >
            {account.plan === "active" ? t.account.plans : t.account.seePlans}
            <ArrowRight size={16} />
          </Link>
        )}
      </div>
      {account.billing && !config.preview && (
        <button className="sheet-row" disabled={opening} onClick={portal}>
          {opening ? (
            <LoaderCircle size={17} className="spin" />
          ) : (
            <ReceiptText size={17} />
          )}{" "}
          {account.subscription ? t.account.billing : t.account.invoices}
          <ExternalLink size={14} className="sheet-out" />
        </button>
      )}
      <div className="sheet-row setting">
        <Languages size={17} /> {t.account.language}
        <LanguageSwitch />
      </div>
      {config.support && (
        <a className="sheet-row" href={`mailto:${config.support}`}>
          <LifeBuoy size={17} /> {t.account.help} · {config.support}
        </a>
      )}
      <Link className="sheet-row" to="/legal" onClick={onClose}>
        <Scale size={17} /> {t.account.legal}
      </Link>
      <button
        className="sheet-row"
        onClick={() => void post("/auth/logout").then(onSignedOut)}
      >
        <LogOut size={17} /> {t.account.signOut}
      </button>
    </Modal>
  );
}

function SignIn({
  config,
  onLogin,
}: {
  config: StudioConfig;
  onLogin: (a: Account, notice?: string) => void;
}) {
  const language = useLang(),
    t = useText(TEXT).welcome;
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const rings = useRef<HTMLCanvasElement>(null);
  // usage pings (src/usage.ts) while signed out; a deep link to anything else shows this page as "/entrar"
  useEffect(() => {
    const path = window.location.pathname;
    return watchPage(
      ["/canjear", "/verify", "/reset", "/empezar"].includes(path)
        ? path
        : "/entrar",
      language,
    );
    // the language is followed by the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => track("lang", language), [language]);
  return (
    <div className="welcome">
      <canvas ref={rings} className="welcome-rings" aria-hidden="true" />
      <header className="welcome-top">
        <Link to="/" aria-label="foco">
          <Logo />
        </Link>
        {config?.preview && <span className="preview-pill">{t.preview}</span>}
        <LanguageSwitch />
        <Link className="welcome-plans" to="/plans">
          {t.plans}
        </Link>
      </header>
      <div className="welcome-stage">
        <PromoPhone rings={rings} />
      </div>
      <section className="welcome-panel">
        <h1 className="welcome-title">
          {t.title}
          <br />
          <span className="focus-word">{t.focus}</span>
        </h1>
        <AccountPanel config={config} onLogin={onLogin} />
        {config?.preview && (
          <Button
            className="ghost large full"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void post("/auth/preview")
                .then(({ account }) => onLogin(account))
                .catch((e) => {
                  setError(e.message);
                  setBusy(false);
                });
            }}
          >
            {t.explore}
          </Button>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {(config?.preview || !config?.signup) && (
          <p className="welcome-note">
            {config?.preview ? t.previewNote : t.invite}
          </p>
        )}
      </section>
    </div>
  );
}

function Library({
  projects,
  onError,
}: {
  projects: ProjectSummary[] | null;
  onError: Notice;
}) {
  const [films, setFilms] = useState<(Film | Preview)[] | null>(null),
    [filter, setFilter] = useState(""),
    [selected, setSelected] = useState<Film | Preview | null>(null);
  const t = useText(TEXT);
  const navigate = useNavigate();
  const active = (projects || []).filter((p) => !p.archived && p.versionCount);
  const key = active.map((p) => `${p.id}:${p.versionCount}`).join(",");
  useEffect(() => {
    if (!projects) return;
    void Promise.all(
      projects
        .filter((p) => !p.archived && p.versionCount)
        .map((p) => api<ProjectDetail>(`/projects/${p.id}`)),
    )
      .then((details) =>
        setFilms(
          details
            .flatMap((d) => [...d.films, ...unrendered(d.films, d.previews)])
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
        ),
      )
      .catch((e) => onError(e.message));
    // Refetch only when the set of projects with videos changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, onError]);
  const byId = new Map((projects || []).map((p) => [p.id, p]));
  const shown = (films || []).filter((f) => !filter || f.projectId === filter);
  const download = (v: Preview) =>
    void post(`/projects/${v.projectId}/previews/${v.id}/render`)
      .then(() => navigate(`/project/${v.projectId}?download=${v.id}`))
      .catch((e) => onError(e.message));
  return (
    <div className="page page-enter">
      <header className="page-head">
        <h1 className="h1">{t.library.title}</h1>
        {active.length > 1 && (
          <div className="filters" role="group" aria-label={t.library.filter}>
            <button
              className="chip"
              aria-pressed={!filter}
              onClick={() => setFilter("")}
            >
              {t.library.all}
            </button>
            {active.map((p) => (
              <button
                className="chip"
                key={p.id}
                aria-pressed={filter === p.id}
                onClick={() => setFilter(p.id)}
              >
                <ProductTile name={p.brand || p.title} />
                {p.brand || p.title}
              </button>
            ))}
          </div>
        )}
      </header>
      {films === null ? (
        <div className="vgrid lib" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <span className="vt skeleton" key={i} />
          ))}
        </div>
      ) : shown.length ? (
        <div className="vgrid lib">
          {shown.map((f) => {
            const p = byId.get(f.projectId);
            return (
              <button className="vc" onClick={() => setSelected(f)} key={f.id}>
                <span className="vt">
                  {f.poster ? (
                    <img src={f.poster} alt="" loading="lazy" />
                  ) : (
                    <span className="vt-empty">
                      <Mark />
                    </span>
                  )}
                  {"render" in f ? (
                    <span className="vt-badge">{t.library.preview}</span>
                  ) : (
                    f.sample && (
                      <span className="vt-badge">{t.library.example}</span>
                    )
                  )}
                  <span className="vt-dur mono">{formatTime(f.duration)}</span>
                </span>
                <span className="vc-title">{f.title}</span>
                <span className="vc-meta">
                  <ProductTile name={p?.brand || p?.title || f.title} />
                  <span>{p?.brand || p?.title}</span>
                  <span aria-hidden="true">·</span>
                  <span>{relativeTime(f.createdAt)}</span>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <span className="empty-phone">
            <Mark />
          </span>
          <p>{t.library.empty}</p>
          <Button className="primary" onClick={() => navigate("/new")}>
            {t.library.make} <ArrowRight size={16} />
          </Button>
        </div>
      )}
      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)}>
          {"render" in selected ? (
            <>
              <PreviewPlayer preview={selected} compact />
              <Button
                className="light full sheet-action"
                onClick={() => download(selected)}
              >
                <ArrowDownToLine size={17} /> {t.download.video}
              </Button>
            </>
          ) : (
            <>
              <FilmPlayer film={selected} compact />
              <a
                className="button light full sheet-action"
                href={`${selected.url}?download=1`}
                download
              >
                <ArrowDownToLine size={17} /> {t.library.downloadMp4}
              </a>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function ProjectScreen({
  account,
  config,
  onError,
  onChange,
}: {
  account: Account;
  config: StudioConfig;
  onError: Notice;
  onChange: () => void;
}) {
  const t = useText(TEXT);
  const senal = useSenal(account, config);
  const { projectId = "" } = useParams();
  const location = useLocation();
  const draftKey = `cutline:draft:${account.id}:${projectId}`;
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ProjectDetail | null>(null),
    [loadError, setLoadError] = useState(""),
    [activity, setActivity] = useState(""),
    // Where the project's requests stand while there is no room for them.
    [line, setLine] = useState<{
      studio: Place | null;
      renders: Record<string, Place>;
    }>({ studio: null, renders: {} }),
    [draft, setDraft] = useState(() => {
      try {
        return sessionStorage.getItem(draftKey) || "";
      } catch {
        return "";
      }
    }),
    [sending, setSending] = useState(false),
    [uploading, setUploading] = useState(false),
    [settings, setSettings] = useState(false),
    [assetsOpen, setAssetsOpen] = useState(false),
    [menu, setMenu] = useState(false),
    [deleting, setDeleting] = useState<"ask" | "busy" | "">(""),
    [selectedId, setSelectedId] = useState(""),
    // A preview the customer asked to download: it downloads once it is ready.
    [wanted, setWanted] = useState(
      () => new URLSearchParams(location.search).get("download") || "",
    ),
    [fresh, setFresh] = useState<Set<string>>(new Set()),
    [collapsed, setCollapsed] = useState(false),
    [surface, setSurface] = useState<"claude" | "shell">("claude"),
    [sideTab, setSideTab] = useState<"video" | "assets">("video"),
    [focus, setFocus] = useState<"work" | "side">("work"),
    [videoNew, setVideoNew] = useState(false),
    [playKey, setPlayKey] = useState(0),
    [refreshing, setRefreshing] = useState(false),
    [streamConnected, setStreamConnected] = useState(true);
  const scroll = useRef<HTMLDivElement>(null),
    nearBottom = useRef(true),
    fileInput = useRef<HTMLInputElement>(null),
    textArea = useRef<HTMLTextAreaElement>(null),
    seen = useRef<Set<string> | null>(null),
    stageEl = useRef<HTMLDivElement>(null),
    anchor = useRef<HTMLDivElement>(null),
    rings = useRef<HTMLCanvasElement>(null),
    field = useRef<RingField | null>(null);
  const wide = useMedia("(min-width: 1181px)");
  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError("");
    setSelectedId("");
    setLine({ studio: null, renders: {} });
    seen.current = null;
    // A stream sends no headers: the language rides on the address.
    const source = new EventSource(
      `/api/projects/${projectId}/events?lang=${lang()}`,
    );
    source.onopen = () => setStreamConnected(true);
    source.onerror = () => setStreamConnected(false);
    source.onmessage = (e) => {
      const event = JSON.parse(e.data) as StudioEvent;
      if (event.type === "snapshot") setDetail(event.detail);
      if (event.type === "project")
        setDetail((d) => (d ? { ...d, project: event.project } : d));
      if (event.type === "message")
        setDetail((d) => {
          if (!d) return d;
          return {
            ...d,
            messages: d.messages.some((m) => m.id === event.message.id)
              ? d.messages.map((m) =>
                  m.id === event.message.id ? event.message : m,
                )
              : [...d.messages, event.message],
          };
        });
      if (event.type === "films")
        setDetail((d) => (d ? { ...d, films: event.films } : d));
      if (event.type === "previews")
        setDetail((d) => (d ? { ...d, previews: event.previews } : d));
      if (event.type === "activity") setActivity(event.text);
      if (event.type === "line")
        setLine((l) => ({
          studio: event.studio === undefined ? l.studio : event.studio,
          renders: event.renders ?? l.renders,
        }));
    };
    void api<ProjectDetail>(`/projects/${projectId}`)
      .then((d) => {
        if (!cancelled) setDetail((current) => current || d);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e.message);
          source.close();
        }
      });
    return () => {
      cancelled = true;
      source.close();
    };
  }, [projectId]);
  // A version that arrives while the project is open resolves out of static.
  useEffect(() => {
    if (!detail) return;
    const ids = [...detail.films, ...detail.previews].map((v) => v.id);
    if (!seen.current) {
      seen.current = new Set(ids);
      return;
    }
    const arrived = ids.filter((id) => !seen.current!.has(id));
    if (!arrived.length) return;
    arrived.forEach((id) => seen.current!.add(id));
    setFresh((f) => new Set([...f, ...arrived]));
    setSelectedId("");
    setVideoNew(true);
    field.current?.emit();
  }, [detail]);
  // In Your Claude, a version that landed out of sight resolves when watched.
  const terminalMode =
    config.nativeClaude && detail?.project.mode === "terminal";
  const videoVisible =
    !terminalMode || ((wide || focus === "side") && sideTab === "video");
  useEffect(() => {
    if (!videoNew || !videoVisible) return;
    setVideoNew(false);
    if (terminalMode) setPlayKey((k) => k + 1);
  }, [videoNew, videoVisible, terminalMode]);
  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey, draft);
    } catch {
      /* Browser storage is optional. */
    }
  }, [draftKey, draft]);
  useEffect(() => {
    if (nearBottom.current && scroll.current)
      scroll.current.scrollTo({
        top: scroll.current.scrollHeight,
        behavior: "instant",
      });
  }, [
    detail?.messages.at(-1)?.text,
    detail?.messages.at(-1)?.progress?.steps.length,
    detail?.messages.length,
    activity,
  ]);
  // The request lives in state; a reload must not download again.
  useEffect(() => {
    if (location.search) navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const v = detail?.previews.find((p) => p.id === wanted);
    if (!v) return;
    if (v.render.state === "failed") setWanted("");
    const film = detail!.films.find((f) => f.id === v.render.filmId);
    if (!film) return;
    setWanted("");
    const a = document.createElement("a");
    a.href = `${film.url}?download=1`;
    a.download = "";
    a.click();
  }, [detail, wanted]);
  const working = detail?.project.status === "working";
  const turn = working
    ? detail?.messages.findLast((m) => m.state === "streaming")?.progress
    : undefined;
  // In line for a workspace, nothing is being made yet.
  const waiting = detail?.project.machineState === "waiting";
  const making = useMaking(waiting ? undefined : turn);
  const language = useLang();
  /** A request in line: its place, or only that it waits until the place is known. */
  const inLine = (place?: Place | null) =>
    place ? lineText(place, language) : LINE[language].waiting;
  const hasDetail = !!detail;
  const status = detail?.project.status,
    versionCount = detail && detail.films.length + detail.previews.length;
  // Keep the sidebar and studio in step with live status and new versions.
  useEffect(() => {
    if (status) onChange();
  }, [status, versionCount, onChange]);
  useEffect(() => {
    if (!hasDetail || !rings.current || !anchor.current) return;
    const f = new RingField(rings.current, anchor.current);
    field.current = f;
    f.fit();
    return () => {
      f.dispose();
      field.current = null;
    };
  }, [hasDetail]);
  useEffect(() => {
    field.current?.pulse(!!working);
  }, [working, hasDetail]);
  const update = async (data: Partial<Project>) => {
    try {
      const { project } = await patch(`/projects/${projectId}`, data);
      setDetail((d) => (d ? { ...d, project } : d));
      onChange();
      return true;
    } catch (e) {
      onError((e as Error).message);
      return false;
    }
  };
  const remove = async () => {
    setDeleting("busy");
    try {
      await api(`/projects/${projectId}`, { method: "DELETE" });
      try {
        sessionStorage.removeItem(draftKey);
      } catch {}
      onChange();
      navigate("/");
      onError(t.project.deleted);
    } catch (e) {
      setDeleting("");
      onError((e as Error).message);
    }
  };
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!draft.trim() || sending || uploading || working) return;
    nearBottom.current = true;
    setSending(true);
    try {
      await post(`/projects/${projectId}/messages`, { text: draft });
      setDraft("");
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSending(false);
      textArea.current?.focus();
    }
  };
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const result = await uploadFiles(projectId, Array.from(files));
      setDetail((d) =>
        d
          ? {
              ...d,
              assets: [
                ...d.assets.filter(
                  (a) => !result.assets.some((b) => a.id === b.id),
                ),
                ...result.assets,
              ],
            }
          : d,
      );
      onError(result.warning || t.project.added(result.assets.length));
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };
  const dragging = useFileDrop((files) => void upload(files), !uploading);
  const collect = () => {
    setRefreshing(true);
    void post(`/projects/${projectId}/collect`)
      .then(({ films, previews }) =>
        setDetail((d) => (d ? { ...d, films, previews } : d)),
      )
      .catch((e) => onError(e.message))
      .finally(() => setRefreshing(false));
  };
  if (loadError)
    return (
      <div className="not-found">
        <Mark />
        <h1 className="h1">{t.project.failed}</h1>
        <p>{loadError}</p>
        <Link className="button ghost" to="/">
          {t.project.back}
        </Link>
      </div>
    );
  if (!detail) return <Loading />;
  const { project, messages, films, previews, assets } = detail;
  // An unrendered preview is a version too; a rendered one gives way to its video.
  const versions: Version[] = [
    ...films.map((f) => ({ ...versionOf(f), film: f })),
    ...unrendered(films, previews).map((v) => ({
      ...versionOf(v),
      preview: v,
    })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const current = versions.find((v) => v.id === selectedId) || versions[0];
  const film = current?.film,
    preview = current?.preview;
  const version = (v: Version) => versions.length - versions.indexOf(v);
  const action = (film || preview) && (
    <DownloadAction
      projectId={projectId}
      film={film}
      preview={preview}
      inLine={
        waiting
          ? inLine(line.studio)
          : preview && line.renders[preview.id]
            ? inLine(line.renders[preview.id])
            : ""
      }
      onPrepare={setWanted}
      onError={onError}
    />
  );
  const images = assets.filter((a) => a.mime.startsWith("image/"));
  const terminal = terminalMode;
  const extraSpend =
    project.footage.use === "yes" || project.music === "original";
  const workspaceLabel =
    config.workspace === "docker"
      ? t.project.env.docker
      : config.workspace === "preview"
        ? t.project.env.preview
        : t.project.env.remote;
  const workTabs = (
    [
      ["claude", "Claude Code", SquareTerminal],
      ...(config.workspace === "preview"
        ? []
        : ([["shell", t.project.shell, ShellIcon]] as const)),
    ] as const
  ).map(([id, label, Icon]) => (
    <button
      key={id}
      role="tab"
      aria-selected={(wide || focus === "work") && surface === id}
      aria-controls="panel-work"
      onClick={() => {
        setSurface(id);
        setFocus("work");
      }}
    >
      <Icon size={15} /> {label}
    </button>
  ));
  const refreshVideos = (
    <IconButton
      className="refresh-videos"
      label={t.project.refresh}
      onClick={collect}
      disabled={refreshing}
    >
      <RefreshCw size={15} className={refreshing ? "spin" : ""} />
    </IconButton>
  );
  const sideTabs = (["video", "assets"] as const).map((id) => (
    <button
      key={id}
      role="tab"
      aria-selected={(wide || focus === "side") && sideTab === id}
      aria-controls="panel-side"
      onClick={() => {
        setSideTab(id);
        setFocus("side");
      }}
    >
      {id === "video" ? <Clapperboard size={15} /> : <FolderOpen size={15} />}
      {id === "video" ? t.project.video : t.assets.title}
      {(id === "video" ? films : assets).length > 0 && (
        <span className="ptab-n mono">
          {(id === "video" ? films : assets).length}
        </span>
      )}
      {id === "video" && videoNew && !videoVisible && (
        <>
          <span className="ptab-new" aria-hidden="true" />
          <span className="sr-only">{t.project.newVersion}</span>
        </>
      )}
    </button>
  ));
  return (
    <div
      className="project page-enter"
      data-mode={terminal ? "terminal" : "chat"}
    >
      {dragging && <DropVeil label={t.project.drop} />}
      <header className="p-head">
        <div className="crumb">
          <Link to="/" className="icon-button back" aria-label={t.project.back}>
            <ArrowLeft size={18} />
          </Link>
          {project.brand && (
            <span className="crumb-brand">
              <ProductTile name={project.brand} />
              {project.brand}
              <span className="crumb-sl">/</span>
            </span>
          )}
          <InlineTitle
            title={project.title}
            onSave={(title) => update({ title })}
          />
          {project.sample && (
            <span className="vt-badge inline">{t.project.example}</span>
          )}
        </div>
        {config.nativeClaude && (
          <div className="p-mode">
            <ModeSwitch
              mode={project.mode}
              disabled={working}
              onChange={(mode) => {
                rememberMode(account.id, mode);
                void update({ mode });
              }}
            />
          </div>
        )}
        <div className="acts">
          <IconButton
            label={t.project.assets}
            onClick={() => setAssetsOpen(true)}
          >
            <FolderOpen size={18} />
            {assets.length > 0 && (
              <span className="badge">{assets.length}</span>
            )}
          </IconButton>
          {(!wide || terminal) && (
            <IconButton
              label={t.project.settings}
              className={extraSpend ? "on" : ""}
              onClick={() => setSettings(true)}
            >
              <SlidersHorizontal size={18} />
              {extraSpend && <span className="dot" />}
            </IconButton>
          )}
          <div className="menu-wrap">
            <IconButton
              label={t.project.actions}
              aria-expanded={menu}
              onClick={() => setMenu(!menu)}
            >
              <MoreHorizontal size={19} />
            </IconButton>
            {menu && (
              <div className="dropdown" onMouseLeave={() => setMenu(false)}>
                <Link
                  to={`/new?from=${projectId}`}
                  onClick={() => setMenu(false)}
                >
                  <Plus size={15} /> {t.project.another}
                </Link>
                <button
                  onClick={() => {
                    void update({ archived: !project.archived }).then((ok) => {
                      if (ok) navigate("/");
                    });
                    setMenu(false);
                  }}
                >
                  <Archive size={15} />
                  {project.archived ? t.project.unarchive : t.project.archive}
                </button>
                <button
                  onClick={() => {
                    setMenu(false);
                    setDeleting("ask");
                  }}
                >
                  <Trash2 size={15} /> {t.project.delete}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {config.nativeClaude && (
        <div className="m-mode">
          <ModeSwitch
            mode={project.mode}
            disabled={working}
            onChange={(mode) => {
              rememberMode(account.id, mode);
              void update({ mode });
            }}
          />
        </div>
      )}
      <div className="p-body">
        {terminal && wide && (
          <>
            <div className="ptabs work-tabs">
              <div
                className="ptabs-list"
                role="tablist"
                aria-label={t.project.workspace}
              >
                {workTabs}
              </div>
              <span className="ptabs-env mono">{workspaceLabel}</span>
            </div>
            <div className="ptabs side-tabs">
              <div
                className="ptabs-list"
                role="tablist"
                aria-label={t.project.side}
              >
                {sideTabs}
              </div>
              {sideTab === "video" && refreshVideos}
            </div>
          </>
        )}
        {terminal && !wide && (
          <div className="ptabs all-tabs">
            <div
              className="ptabs-list"
              role="tablist"
              aria-label={t.project.workspace}
            >
              {workTabs}
              {sideTabs}
            </div>
          </div>
        )}
        <section
          className="p-left"
          aria-label={t.project.work}
          id="panel-work"
          role={terminal ? "tabpanel" : undefined}
          hidden={terminal && !wide && focus === "side"}
        >
          {terminal ? (
            <Suspense
              fallback={
                <div className="terminal-loading">{t.project.opening}</div>
              }
            >
              <TerminalPanel
                projectId={projectId}
                preview={config.workspace === "preview"}
                workspace={config.workspace}
                surface={surface}
              />
            </Suspense>
          ) : (
            <>
              <div
                className="chat scroll"
                tabIndex={0}
                role="region"
                aria-label={t.chat.history}
                ref={scroll}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  nearBottom.current =
                    el.scrollHeight - el.scrollTop - el.clientHeight < 90;
                }}
              >
                {messages.length === 0 && !working ? (
                  <div className="chat-empty">
                    <Mark />
                    <p>{t.chat.empty}</p>
                  </div>
                ) : (
                  messages.map((m) =>
                    m.role === "user" ? (
                      <article className="msg me" key={m.id}>
                        <div className="bub">
                          <Suspense fallback={<p>{m.text}</p>}>
                            <MessageContent text={m.text} />
                          </Suspense>
                        </div>
                      </article>
                    ) : m.role === "assistant" ? (
                      <article
                        className={`msg dir ${m.state === "error" ? "msg-error" : ""}`}
                        key={m.id}
                      >
                        <span
                          className="who"
                          role="img"
                          aria-label={t.chat.director}
                        >
                          <Mark />
                        </span>
                        <div className="body">
                          {m.progress && (
                            <Trace
                              progress={m.progress}
                              activity={
                                m.state !== "streaming"
                                  ? ""
                                  : waiting
                                    ? inLine(line.studio)
                                    : activity
                              }
                            />
                          )}
                          <Suspense fallback={<p>{m.text}</p>}>
                            <MessageContent text={m.text} />
                          </Suspense>
                          {m.state === "streaming" && m.text && (
                            <span className="caret" />
                          )}
                        </div>
                      </article>
                    ) : (
                      <p className="msg sys" key={m.id}>
                        {m.text}
                      </p>
                    ),
                  )
                )}
              </div>
              {versions.length > 0 && !working && (
                <div className="refine">
                  <button
                    className="chip"
                    onClick={() => {
                      setDraft(t.chat.shorter.ask);
                      textArea.current?.focus();
                    }}
                  >
                    {t.chat.shorter.label}
                  </button>
                  <button
                    className="chip"
                    onClick={() => {
                      setDraft(t.chat.opening.ask);
                      textArea.current?.focus();
                    }}
                  >
                    {t.chat.opening.label}
                  </button>
                  <button
                    className="chip"
                    onClick={() => {
                      const ask = t.chat.language.ask;
                      setDraft(ask);
                      requestAnimationFrame(() => {
                        textArea.current?.focus();
                        textArea.current?.setSelectionRange(
                          ask.length,
                          ask.length,
                        );
                      });
                    }}
                  >
                    {t.chat.language.label}
                  </button>
                  <button
                    className="chip"
                    onClick={() => {
                      setDraft(t.chat.read.ask);
                      textArea.current?.focus();
                    }}
                  >
                    {t.chat.read.label}
                  </button>
                  <Link
                    className="chip"
                    to={`/new?from=${projectId}&idea=feature`}
                  >
                    {t.chat.feature}
                  </Link>
                  <Link
                    className="chip"
                    to={`/new?from=${projectId}&idea=series`}
                  >
                    {t.chat.series}
                  </Link>
                </div>
              )}
              <form className="ccomp" onSubmit={send}>
                <IconButton
                  label={t.chat.attach}
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <RefreshCw size={17} className="spin" />
                  ) : (
                    <Plus size={19} />
                  )}
                </IconButton>
                <textarea
                  ref={textArea}
                  aria-label={t.chat.message}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onFocus={() => setCollapsed(true)}
                  onBlur={() => setCollapsed(false)}
                  placeholder={messages.length ? t.chat.change : t.chat.first}
                  rows={1}
                  maxLength={20000}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing
                    ) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                />
                {working ? (
                  <button
                    type="button"
                    className="send stop"
                    aria-label={t.chat.stop}
                    onClick={() =>
                      void post(`/projects/${projectId}/stop`).catch((e) =>
                        onError(e.message),
                      )
                    }
                  >
                    <Square size={13} fill="currentColor" />
                  </button>
                ) : (
                  <button
                    className="send"
                    disabled={!draft.trim() || sending || uploading}
                    aria-label={t.chat.send}
                  >
                    <ArrowUp size={19} strokeWidth={2.2} />
                  </button>
                )}
              </form>
              {!streamConnected && (
                <p className="reconnecting" role="status">
                  {t.chat.reconnecting}
                </p>
              )}
            </>
          )}
        </section>
        <section
          className={`stage ${collapsed ? "col" : ""} ${terminal ? `side-${sideTab}` : ""}`}
          aria-label={t.stage.room}
          id="panel-side"
          role={terminal ? "tabpanel" : undefined}
          hidden={terminal && !wide && focus === "work"}
          ref={stageEl}
        >
          <div className="stage-main">
            <canvas ref={rings} className="stage-rings" aria-hidden="true" />
            <div className="pwrap" ref={anchor}>
              {film ? (
                <FilmPlayer
                  key={`${film.id}:${playKey}`}
                  film={film}
                  fresh={fresh.has(film.id)}
                />
              ) : preview ? (
                <PreviewPlayer
                  key={`${preview.id}:${playKey}`}
                  preview={preview}
                  fresh={fresh.has(preview.id)}
                />
              ) : (
                <div className="player">
                  <div
                    className={`phone ${working ? "making-phone" : "empty"}`}
                  >
                    <div className="screen">
                      {working ? (
                        <MakingScreen
                          progress={turn}
                          inLine={
                            waiting
                              ? {
                                  place: line.studio,
                                  text: inLine(line.studio),
                                }
                              : undefined
                          }
                        />
                      ) : (
                        <span className="empty-screen">
                          <Mark />
                        </span>
                      )}
                    </div>
                  </div>
                  {!working && <p className="stage-caption">{t.stage.first}</p>}
                </div>
              )}
            </div>
            {action && <div className="stage-action">{action}</div>}
            {action && senal && (
              <SenalHint site={config.senal} className="stage-hint" />
            )}
          </div>
          {(versions.length > 0 || working || (terminal && !wide)) && (
            <div
              className="vchips noscroll"
              role="group"
              aria-label={t.stage.versions}
            >
              {working && (
                <span className="vchip pending">
                  <Ring /> v{versions.length + 1}
                  {!waiting && <span className="mono">{making.percent}%</span>}
                </span>
              )}
              {versions.map((v) => (
                <button
                  key={v.id}
                  className={`vchip ${v.preview ? "is-preview" : ""}`}
                  aria-current={current?.id === v.id}
                  aria-label={t.stage.version(version(v), !!v.preview)}
                  onClick={() => setSelectedId(v.id)}
                >
                  v{version(v)}{" "}
                  <span className="mono">{formatTime(v.duration)}</span>
                </button>
              ))}
              <span className="vchips-sp" />
              {terminal && !wide && refreshVideos}
              {action && <span className="m-action">{action}</span>}
            </div>
          )}
          {action && senal && (
            <SenalHint site={config.senal} className="m-hint" />
          )}
          <aside className="st-side scroll" aria-label={t.stage.details}>
            <div className="st-versions">
              <div className="sb-h">
                <h2 className="h2">{t.stage.versions}</h2>
                <IconButton
                  label={t.project.refresh}
                  onClick={collect}
                  disabled={refreshing}
                >
                  <RefreshCw size={15} className={refreshing ? "spin" : ""} />
                </IconButton>
              </div>
              <div className="film-versions">
                {working && (
                  <div className="vrow pending">
                    <span className="vt">
                      <MakingThumb
                        poster={versions[0]?.poster || undefined}
                        percent={waiting ? undefined : making.percent}
                      />
                    </span>
                    <span className="vi">
                      <b>
                        v{versions.length + 1}
                        {!waiting && (
                          <span className="mono">{making.percent}%</span>
                        )}
                      </b>
                      <small>
                        {waiting ? inLine(line.studio) : making.stage}
                      </small>
                    </span>
                  </div>
                )}
                {versions.map((v) => (
                  <button
                    key={v.id}
                    className={`vrow ${fresh.has(v.id) ? "fresh" : ""}`}
                    aria-current={current?.id === v.id}
                    onClick={() => setSelectedId(v.id)}
                  >
                    <span className="vt">
                      {v.poster ? <img src={v.poster} alt="" /> : <Mark />}
                    </span>
                    <span className="vi">
                      <b>
                        v{version(v)}
                        <span className="mono">{formatTime(v.duration)}</span>
                        <span className="when">
                          {relativeTime(v.createdAt)}
                        </span>
                      </b>
                      <small>
                        {v.preview
                          ? v.preview.render.state === "rendering"
                            ? waiting
                              ? inLine(line.studio)
                              : line.renders[v.id]
                                ? inLine(line.renders[v.id])
                                : t.stage.preparing(
                                    Math.round(v.preview.render.progress * 100),
                                  )
                            : t.stage.preview(v.title)
                          : v.title}
                      </small>
                    </span>
                  </button>
                ))}
                {!versions.length && !working && (
                  <p className="side-empty">{t.stage.none}</p>
                )}
              </div>
            </div>
            <div className="sb-h">
              <h2 className="h2">{t.assets.title}</h2>
              <button
                className="text-button"
                onClick={() => setAssetsOpen(true)}
              >
                {assets.length ? t.assets.all : t.assets.add}
              </button>
            </div>
            <div className="side-assets">
              {(terminal ? images : images.slice(0, 6)).map((a) => (
                <span className="at" key={a.id}>
                  <img src={a.url} alt={a.name} />
                </span>
              ))}
              {assets
                .filter((a) => !a.mime.startsWith("image/"))
                .slice(
                  0,
                  terminal
                    ? undefined
                    : Math.max(0, 4 - Math.min(images.length, 6)),
                )
                .map((a) => (
                  <span className="arow" key={a.id}>
                    <FileText size={14} />
                    <span>{a.name}</span>
                  </span>
                ))}
              {(!assets.length || terminal) && (
                <button
                  className="upl"
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload size={15} />
                  <b>{t.assets.drop}</b>
                  <small>{t.assets.kinds}</small>
                </button>
              )}
            </div>
            <div className="sb-h">
              <h2 className="h2">{t.stage.settings}</h2>
            </div>
            <VideoSummary project={project} onOpen={() => setSettings(true)} />
          </aside>
        </section>
      </div>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => void upload(e.target.files)}
      />
      {deleting && (
        <Modal
          title={t.remove.title}
          subtitle={project.title}
          className="confirm"
          onClose={() => deleting === "ask" && setDeleting("")}
        >
          <p className="muted">{t.remove.note}</p>
          <div className="confirm-actions">
            <Button
              className="ghost"
              disabled={deleting === "busy"}
              onClick={() => setDeleting("")}
            >
              {t.remove.cancel}
            </Button>
            <Button
              className="danger"
              disabled={deleting === "busy"}
              onClick={() => void remove()}
            >
              {deleting === "busy" ? t.remove.busy : t.remove.confirm}
            </Button>
          </div>
        </Modal>
      )}
      {settings && (
        <VideoSettings
          value={project}
          projectId={project.id}
          preview={config.preview}
          onClose={() => setSettings(false)}
          onSave={async (choices) => {
            const { project: p } = await patch(
              `/projects/${project.id}/production`,
              choices,
            );
            setDetail((d) => (d ? { ...d, project: p } : d));
            onChange();
          }}
        />
      )}
      {assetsOpen && (
        <Modal title={t.assets.title} onClose={() => setAssetsOpen(false)}>
          <button
            className="upload-area"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            <Upload size={20} />
            <b>{uploading ? t.assets.adding : t.assets.choose}</b>
            <small>{t.assets.kinds}</small>
          </button>
          {assets.length > 0 && (
            <div className="asset-list">
              {assets.map((a) => (
                <a href={a.url} key={a.id} download>
                  <span className="asset-icon">
                    {a.mime.startsWith("image/") ? (
                      <img src={a.url} alt="" />
                    ) : (
                      <ImageIcon size={16} />
                    )}
                  </span>
                  <span className="asset-name">
                    <b>{a.name}</b>
                    <small className="mono">{formatSize(a.size)}</small>
                  </span>
                  <ArrowDownToLine size={16} />
                </a>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/** A video, or a preview that has not been rendered yet, as the versions list shows it. */
type Version = {
  id: string;
  title: string;
  duration: number;
  createdAt: string;
  poster: string | null;
  film?: Film;
  preview?: Preview;
};
const versionOf = (v: Film | Preview) => ({
  id: v.id,
  title: v.title,
  duration: v.duration,
  createdAt: v.createdAt,
  poster: v.poster,
});

/** A preview's next step: render the 60 fps video, with live progress. */
/** The way to the file. A rendered video downloads at once; a preview is
 * prepared first and then downloads by itself. */
function DownloadAction({
  projectId,
  film,
  preview,
  inLine,
  onPrepare,
  onError,
}: {
  projectId: string;
  film?: Film;
  preview?: Preview;
  /** Where its render waits in line, said; empty when it does not wait. */
  inLine: string;
  onPrepare: (previewId: string) => void;
  onError: (message: string) => void;
}) {
  const t = useText(TEXT);
  const [starting, setStarting] = useState(false);
  if (film)
    return (
      <a className="button primary dl" href={`${film.url}?download=1`} download>
        <ArrowDownToLine size={16} /> {t.download.video}
      </a>
    );
  if (!preview) return null;
  const { state, progress } = preview.render;
  const percent = Math.round(progress * 100);
  if (state === "rendering" && inLine)
    return (
      <span className="render-progress" role="status">
        <Ring /> {inLine}
      </span>
    );
  if (state === "rendering" || state === "done")
    return (
      <span className="render-progress" role="status">
        <span
          className="render-bar"
          style={{ width: `${Math.max(4, percent)}%` }}
        />
        <Ring /> {t.download.preparing}
        <span className="mono">{percent}%</span>
      </span>
    );
  return (
    <button
      className="button primary dl"
      disabled={starting}
      title={preview.render.error}
      onClick={() => {
        setStarting(true);
        onPrepare(preview.id);
        void post(`/projects/${projectId}/previews/${preview.id}/render`)
          .catch((e) => {
            onPrepare("");
            onError(e.message);
          })
          .finally(() => setStarting(false));
      }}
    >
      <ArrowDownToLine size={16} />
      {state === "failed" ? t.download.again : t.download.video}
    </button>
  );
}
